/* global Liferay */
import { forwardRef, useCallback, useEffect, useState } from "react";
import ClayLoadingIndicator from "@clayui/loading-indicator";
import { getNavigationMenu } from "../../services/menu";
import { deleteLessonNavigationCookies } from "../../utils/util";
import { getCompletedAssets } from "../../services/enrollment";
import { getCourseByAssetTypeAndId } from "../../services/course";
import NavigationModule from "./NavigationModule";
import NavigationModuleItem from "./NavigationModuleItem";
import { OBJECT_ENTRY_URL_SEPARATOR, RELATIONSHIP } from "../../utils/constants";

const NavigationMenu = forwardRef((props) => {
  const { assetId, assetType, moduleTextLabel } = props;

  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCollapsedBar, setIsCollapsedBar] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [navigationMenu, setNavigationMenu] = useState(null);
  const [navigationMenuData, setNavigationMenuData] = useState(null);
  const [moduleLabel, setModuleLabel] = useState(null);
  const [completedAssetIds, setCompletedAssetsIds] = useState("");
  const [courseId, setCourseId] = useState(null);

  useEffect(() => {
    const fetchCourseId = async () => {
      const result = await getCourseByAssetTypeAndId(assetType, assetId);
      setCourseId(result);
    };

    fetchCourseId();
  }, []);

  useEffect(() => {
    const fetchCompletedAssets = async () => {
      const response = await getCompletedAssets(courseId);
      setCompletedAssetsIds(response);
    };

    fetchCompletedAssets();
  }, [courseId]);

  const isLessonCompleted = useCallback(
    (lessonId) => {
      return completedAssetIds && completedAssetIds?.includes(lessonId);
    },
    [completedAssetIds]
  );

  const isModuleCompleted = useCallback(
    (moduleId) => {
      let module = navigationMenuData.find((module) => module.id === moduleId);

      for (let index = 0; index < module.children.length; index++) {
        if (!isLessonCompleted(module.children[index].id)) {
          return false;
        }
      }

      return true;
    },
    [isLessonCompleted, navigationMenuData]
  );

  const getLessonIndex = useCallback(
    (assetId, index) => {
      let totalSingleLesson = 0;

      for (
        let moduleIndex = 0;
        moduleIndex < navigationMenu.length;
        moduleIndex++
      ) {
        let module = navigationMenu[moduleIndex];

        if (module.id === assetId) {
          return index - totalSingleLesson + 1;
        }

        if (module.children.length === 1) {
          totalSingleLesson += 1;
        }
      }

      return -1;
    },
    [navigationMenu]
  );

  const getModuleIndex = useCallback(
    (assetId) => {
      let index = 1;

      for (
        let moduleIndex = 0;
        moduleIndex < navigationMenu.length;
        moduleIndex++
      ) {
        let module = navigationMenu[moduleIndex];

        if (module.id === assetId) {
          return index;
        }

        if (module.children && module.children.length > 1) {
          index++;
        }
      }
    },
    [navigationMenu]
  );

  const prepareMenu = (navigationMenu) => {
    if (navigationMenu && navigationMenu.length > 0) {
      let lastIteration = false;
      let nextLesson = null;
      let previousLesson = null;

      for (
        let moduleIndex = 0;
        moduleIndex < navigationMenu.length;
        moduleIndex++
      ) {
        let module = navigationMenu[moduleIndex];

        module["active"] = false;

        for (
          let lessonIndex = 0;
          lessonIndex < module.children.length;
          lessonIndex++
        ) {
          let lesson = module.children[lessonIndex];

          lesson["active"] = false;

          if (lastIteration) {
            nextLesson = lesson;

            break;
          }

          if (parseInt(lesson.id) === parseInt(assetId)) {
            module["active"] = true;
            lesson["active"] = true;
            lastIteration = true;

            continue;
          }

          previousLesson = lesson;
        }

        if (nextLesson) {
          break;
        }
      }

      deleteLessonNavigationCookies();

      localStorage.setItem(
        `lesson_${assetId}_next`,
        JSON.stringify(nextLesson)
      );
      localStorage.setItem(
        `lesson_${assetId}_previous`,
        JSON.stringify(previousLesson)
      );

      Liferay.fire("lessonNavigationUpdated", {});

      setNavigationMenu(navigationMenu);
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed((prevState) => !prevState);
  };

  useEffect(() => {
    prepareMenu(navigationMenuData);
  }, [navigationMenuData]);

  useEffect(() => {
    setIsLoading(true);

    setModuleLabel(
      moduleTextLabel && moduleTextLabel.length > 0 ? moduleTextLabel : "Module"
    );

    getNavigationMenu(assetId, assetType).then((result) => {
      let modules = result.items[0][RELATIONSHIP.COURSES_TO_MODULES];

      modules = modules.sort(
        (module1, module2) => module1.position - module2.position
      );

      modules.forEach((module) => {
        module["children"] = module[RELATIONSHIP.MODULES_TO_LESSONS];

        module[RELATIONSHIP.MODULES_TO_LESSONS].forEach((item) => {
          item.urlSeparator = OBJECT_ENTRY_URL_SEPARATOR.LESSON;
        });

        module[RELATIONSHIP.MODULES_TO_QUIZZES].forEach((item) => {
          item.urlSeparator = OBJECT_ENTRY_URL_SEPARATOR.QUIZ;
          module.children.push(item);
        });

        module.children.sort(
          (children1, children2) => children1.position - children2.position
        );
      });

      setNavigationMenuData(modules);

      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1024px)");

    const handleMediaQueryChange = (e) => {
      setIsCollapsed(!e.matches);
      setIsCollapsedBar(!e.matches);
    };

    handleMediaQueryChange(mediaQuery);

    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () =>
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
  }, []);

  return (
    <>
      <div className="position-relative z-1">
        <div className={`collapse-bar ${isCollapsedBar ? "hide" : ""}`}>
          <p>Course Overview</p>
          <div className="button-collapse" onClick={toggleCollapse}>
            <p className={`icon-arrow ${isCollapsed ? "rotate" : ""}`}></p>
          </div>
        </div>

        <div className={`nav-menu ${!isCollapsed ? "hide" : ""}`}>
          {isLoading && (
            <span style={{ display: "inline-block" }}>
              <ClayLoadingIndicator displayType="secondary" size="sm" />
            </span>
          )}

          {!isLoading &&
            navigationMenu &&
            navigationMenu.map((navigationMenuItem, index) => {
              return (
                <div key={index}>
                  {navigationMenuItem[RELATIONSHIP.MODULES_TO_LESSONS].length > 1 && (
                    <NavigationModule
                      getLessonIndex={getLessonIndex}
                      getModuleIndex={getModuleIndex}
                      index={index}
                      isLessonCompleted={isLessonCompleted}
                      isModuleCompleted={isModuleCompleted}
                      moduleLabel={moduleLabel}
                      navigationMenuItem={navigationMenuItem}
                    />
                  )}

                  {navigationMenuItem[RELATIONSHIP.MODULES_TO_LESSONS].length === 1 && (
                    <NavigationModuleItem
                      isLessonCompleted={isLessonCompleted}
                      navigationMenuItem={navigationMenuItem}
                    />
                  )}
                </div>
              );
            })}
        </div>
      </div>
    </>
  );
});

export default NavigationMenu;